import React from "react";
import { Box, Container, Grid, Typography, Button } from "@mui/material";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";

const NAVY = "#1B1F3B"; const CORAL = "#FF6B6B"; const YELLOW = "#FFD93D"; const TEAL = "#06D6A0"; const CREAM = "#FFF8F0";

/* Animated reading kids SVG */
const ReadingSVG = () => (
  <svg viewBox="0 0 400 340" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", maxWidth: 400, height: "auto" }}>
    <rect width="400" height="340" rx="20" fill="#FFF0E0" />
    {/* Big book */}
    <g style={{ animation: "floatY 3s ease-in-out infinite", transformOrigin: "200px 200px" }}>
      <rect x="60" y="140" width="280" height="180" rx="10" fill="#fff" stroke={NAVY} strokeWidth="3" />
      <rect x="60" y="140" width="140" height="180" rx="10" fill="#FFF5CC" stroke={NAVY} strokeWidth="2" />
      <rect x="195" y="140" width="4" height="180" fill={NAVY} />
      {/* Lines on pages */}
      {[160, 180, 200, 220, 240, 260].map(y => (
        <g key={y}>
          <rect x="75" y={y} width="110" height="3" rx="2" fill={CORAL} opacity="0.5" />
          <rect x="210" y={y} width="110" height="3" rx="2" fill={TEAL} opacity="0.5" />
        </g>
      ))}
      {/* Bookmark */}
      <polygon points="340,140 360,140 360,175 350,162 340,175" fill={CORAL} stroke={NAVY} strokeWidth="1.5" />
    </g>
    {/* Kid reading - sits on top of book area */}
    <g style={{ animation: "wiggle 3s ease-in-out infinite", transformOrigin: "160px 130px" }}>
      <circle cx="160" cy="105" r="30" fill="#FDBCB4" stroke={NAVY} strokeWidth="2" />
      {/* Hair */}
      <ellipse cx="160" cy="88" rx="28" ry="14" fill={NAVY} />
      {/* Eyes */}
      <circle cx="150" cy="108" r="5" fill="#fff" /><circle cx="152" cy="108" r="3" fill={NAVY} />
      <circle cx="170" cy="108" r="5" fill="#fff" /><circle cx="172" cy="108" r="3" fill={NAVY} />
      {/* Smile */}
      <path d="M153 118 Q160 125 167 118" stroke={NAVY} strokeWidth="2.5" strokeLinecap="round" fill="none" />
      {/* Body */}
      <rect x="140" y="133" width="40" height="38" rx="8" fill={TEAL} stroke={NAVY} strokeWidth="2" />
      {/* Arms holding mini book */}
      <rect x="118" y="140" width="24" height="12" rx="5" fill={TEAL} stroke={NAVY} strokeWidth="1.5" />
      <rect x="158" y="140" width="24" height="12" rx="5" fill={TEAL} stroke={NAVY} strokeWidth="1.5" />
      <rect x="120" y="148" width="60" height="30" rx="6" fill={YELLOW} stroke={NAVY} strokeWidth="2" />
    </g>
    {/* Stars + sparkles */}
    <text x="50" y="80" fontSize="22" style={{ animation: "starSpin 3s linear infinite" }}>⭐</text>
    <text x="330" y="90" fontSize="18" style={{ animation: "starSpin 4s linear infinite reverse" }}>✨</text>
    <text x="80" y="310" fontSize="16" style={{ animation: "floatY 2s ease-in-out infinite" }}>📖</text>
    <text x="300" y="310" fontSize="16" style={{ animation: "floatY 2.5s ease-in-out infinite" }}>✏️</text>
    {/* Floating letters */}
    {["A", "B", "C"].map((l, i) => (
      <text key={l} x={280 + i * 30} y={80 + i * 20} fontFamily="Fredoka One, cursive" fontSize="24" fill={[CORAL, YELLOW, TEAL][i]} style={{ animation: `floatY ${2 + i * 0.5}s ease-in-out infinite` }} stroke={NAVY} strokeWidth="1">{l}</text>
    ))}
  </svg>
);

const AboutSection = ({ schoolInfo, points }) => (
  <AnimatedSection id="about" sx={{ backgroundColor: CREAM, py: { xs: 9, md: 12 } }}>
    <Container maxWidth="xl">
      <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
        {/* Illustration */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: "relative" }}>
            <Box sx={{
              position: "absolute", top: -16, left: -16, width: "100%", height: "100%",
              borderRadius: "24px", border: `4px solid ${NAVY}`, backgroundColor: YELLOW, zIndex: 0
            }} />
            <Box sx={{ position: "relative", zIndex: 1, borderRadius: "24px", border: `3px solid ${NAVY}`, overflow: "hidden", backgroundColor: "#FFF0E0" }}>
              <ReadingSVG />
            </Box>
          </Box>
        </Grid>

        {/* Text */}
        <Grid item xs={12} md={7}>
          <SectionHeading
            eyebrow="About Our School"
            title={`Welcome to ${"\n"}St Lord's! 🏫`}
            description="Every child deserves a school where they feel safe, happy and supported. We create a friendly environment where children can learn, play, discover their talents and grow with confidence."
            eyebrowColor={CORAL}
            eyebrowBg="#FFE8E8"
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
            {points.map((p, i) => (
              <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "flex-start", p: 2.5, borderRadius: "14px", border: `2px solid ${NAVY}`, backgroundColor: "#fff", boxShadow: `4px 4px 0 ${NAVY}` }}>
                <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", backgroundColor: [YELLOW, CORAL, TEAL][i], border: `2px solid ${NAVY}`, display: "grid", placeItems: "center", fontSize: "1.1rem" }}>
                  {["🌟", "🎨", "🚀"][i]}
                </Box>
                <Typography sx={{ fontWeight: 700, color: NAVY, lineHeight: 1.7, fontSize: "0.97rem" }}>{p}</Typography>
              </Box>
            ))}
          </Box>

          {/* Info pills */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 4 }}>
            {[...schoolInfo.boards.map(b => `📋 ${b}`), ...schoolInfo.schoolTypes.map(t => `🏫 ${t}`)].map((tag) => (
              <Box key={tag} sx={{ px: 2, py: 0.6, borderRadius: "8px", border: `2px solid ${NAVY}`, backgroundColor: YELLOW, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: "0.78rem", color: NAVY }}>
                {tag}
              </Box>
            ))}
          </Box>

          <Button variant="contained" size="large" onClick={() => { }}
            sx={{ backgroundColor: TEAL, color: NAVY, border: `2.5px solid ${NAVY}`, boxShadow: `4px 4px 0 ${NAVY}`, "&:hover": { backgroundColor: "#04C48E" } }}>
            Learn More About Us →
          </Button>
        </Grid>
      </Grid>
    </Container>
  </AnimatedSection>
);

export default AboutSection;
